import { useMemo } from 'react';
import ChartContainer from '@/components/charts/ChartContainer';
import PieChart from '@/components/charts/PieChart';
import { UNIVERSAL_STAGES, type StageKey } from '../types';
import type { QuoteOperationsSummary } from '../quoteSummary';

interface QuoteOperationsOverviewCardProps {
  data?: QuoteOperationsSummary;
  loading?: boolean;
}

const STAGE_COLORS = [
  '#2563eb',
  '#0891b2',
  '#16a34a',
  '#65a30d',
  '#ca8a04',
  '#f97316',
  '#dc2626',
  '#9333ea',
  '#4f46e5',
  '#64748b',
];

const STAGE_LABELS = UNIVERSAL_STAGES.reduce<Record<StageKey, string>>((acc, stage) => {
  acc[stage.key] = stage.label;
  return acc;
}, {} as Record<StageKey, string>);

export function QuoteOperationsOverviewCard({ data, loading }: QuoteOperationsOverviewCardProps) {
  const { chartData, colors } = useMemo(() => {
    const entries = Object.entries(data?.byStage ?? {}).filter(([, count]) => Number(count) > 0);
    return {
      chartData: entries.map(([stage, count]) => ({
        name: STAGE_LABELS[stage as StageKey] || stage,
        value: Number(count),
      })),
      colors: entries.map(([stage]) => {
        const index = UNIVERSAL_STAGES.findIndex((item) => item.key === stage);
        return STAGE_COLORS[index] || '#64748b';
      }),
    };
  }, [data]);

  return (
    <ChartContainer title="Quotes by Stage" loading={loading} height={220}>
      {chartData.length > 0 ? (
        <PieChart
          data={chartData}
          height={220}
          donut
          showLegend
          colors={colors}
        />
      ) : (
        <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
          No quote data available
        </div>
      )}
    </ChartContainer>
  );
}
